import React from 'react'
import { Card, CardContent } from '../ui'
import { useI18n } from '../../i18n'
import { featureFlags } from '../../config/featureFlags'
import {
  fetchMaterializedReadiness,
  type MaterializedReadinessPayload,
} from '../../services/complianceTopologyApi'

interface Props {
  systemId: string
  standardCode: string | null
}

function stateBadgeClass(state: string) {
  switch (state) {
    case 'HEALTHY':
      return 'bg-emerald-50 text-emerald-800'
    case 'PARTIAL':
      return 'bg-amber-50 text-amber-800'
    case 'DEGRADED':
    case 'STALE':
      return 'bg-orange-50 text-orange-800'
    case 'FAILED':
    case 'BLOCKED':
      return 'bg-red-50 text-red-800'
    case 'INHERITED':
      return 'bg-indigo-50 text-indigo-800'
    default:
      return 'bg-slate-100 text-slate-600'
  }
}

export default function InformationSystemMaterializedReadinessPanel({ systemId, standardCode }: Props) {
  const { t } = useI18n()
  const [data, setData] = React.useState<MaterializedReadinessPayload | null>(null)
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  React.useEffect(() => {
    if (
      !featureFlags.COMPLIANCE_TOPOLOGY_V1 ||
      !featureFlags.COMPLIANCE_TOPOLOGY_READINESS_V1 ||
      !standardCode
    ) {
      setData(null)
      return
    }
    setLoading(true)
    setError(null)
    void fetchMaterializedReadiness(systemId, standardCode)
      .then((d) => setData(d))
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load readiness'))
      .finally(() => setLoading(false))
  }, [systemId, standardCode])

  if (!featureFlags.COMPLIANCE_TOPOLOGY_V1 || !featureFlags.COMPLIANCE_TOPOLOGY_READINESS_V1) {
    return null
  }
  if (!standardCode) return null

  const r = data?.readiness
  const staleCount =
    data?.evidenceFreshness?.filter((e) => e.freshnessStatus === 'STALE' || e.freshnessStatus === 'EXPIRED')
      .length || 0
  const pressure =
    r?.operationalPressure != null && r.operationalPressure >= 10
      ? 'HIGH'
      : r?.operationalPressure != null && r.operationalPressure >= 5
        ? 'MEDIUM'
        : 'LOW'

  return (
    <section id="passport-section-materialized-readiness" className="scroll-mt-4 mt-4">
      <Card>
        <CardContent className="p-4">
          <h3 className="text-sm font-semibold text-slate-800">
            {t('complianceTopology.readinessBreakdown', 'Readiness breakdown')}
          </h3>
          <p className="text-xs text-slate-500 mt-1">
            {standardCode}
            {r?.readinessStatus ? ` · ${r.readinessStatus}` : ''}
          </p>

          {loading ? <p className="text-xs text-slate-400 mt-2">…</p> : null}
          {error ? <p className="text-xs text-red-600 mt-2">{error}</p> : null}

          {r ? (
            <div className="flex flex-wrap gap-3 mt-2 text-xs text-slate-600">
              {r.evidenceCoverage != null ? (
                <span>
                  {t('complianceTopology.evidenceCoverage', 'Evidence coverage')}: {r.evidenceCoverage}%
                </span>
              ) : null}
              {r.scopedReadiness != null ? (
                <span>
                  {t('complianceTopology.scopedControl', 'Scoped')}: {r.scopedReadiness}%
                </span>
              ) : null}
              <span>
                {t('complianceTopology.operationalPressure', 'Operational pressure')}: {pressure}
              </span>
              {staleCount > 0 ? (
                <span className="text-orange-700">
                  {staleCount} {t('complianceTopology.staleEvidence', 'stale evidence')}
                </span>
              ) : null}
              {(data?.blockers?.length || 0) > 0 ? (
                <span className="text-red-700">
                  {data!.blockers.length}{' '}
                  {t('complianceTopology.certificationBlocker', 'certification blockers')}
                </span>
              ) : null}
            </div>
          ) : null}

          {data?.controlStates?.length ? (
            <div className="mt-3">
              <h4 className="text-xs font-semibold text-slate-700 mb-1">
                {t('complianceTopology.controlState', 'Control states')}
              </h4>
              <ul className="space-y-1 max-h-36 overflow-y-auto">
                {data.controlStates.slice(0, 12).map((c) => (
                  <li
                    key={c.controlCode || 'unknown'}
                    className="text-xs flex items-center gap-2 border border-slate-100 rounded px-2 py-1"
                    title={c.reasoning?.summary}
                  >
                    <span
                      className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${stateBadgeClass(c.operationalState)}`}
                    >
                      {c.operationalState}
                    </span>
                    <span className="font-mono">{c.controlCode}</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {data?.evidenceFreshness?.filter((e) => e.freshnessStatus !== 'FRESH').length ? (
            <div className="mt-3">
              <h4 className="text-xs font-semibold text-slate-700 mb-1">
                {t('complianceTopology.evidenceFreshness', 'Evidence freshness')}
              </h4>
              <ul className="text-xs text-slate-600 space-y-1">
                {data.evidenceFreshness
                  .filter((e) => e.freshnessStatus !== 'FRESH')
                  .slice(0, 5)
                  .map((e) => (
                    <li key={e.evidenceId} title={e.rationale || undefined}>
                      {e.freshnessStatus} — {e.evidenceTitle || e.evidenceId.slice(0, 8)}
                    </li>
                  ))}
              </ul>
            </div>
          ) : null}

          {data?.blockers?.length ? (
            <div className="mt-3">
              <h4 className="text-xs font-semibold text-slate-700 mb-1">
                {t('complianceTopology.certificationBlocker', 'Certification blockers')}
              </h4>
              <ul className="text-xs space-y-1">
                {data.blockers.slice(0, 5).map((b, i) => (
                  <li key={b.id || i} className="text-red-800" title={b.description}>
                    [{b.severity}] {b.title}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {data?.drift?.length ? (
            <div className="mt-3">
              <h4 className="text-xs font-semibold text-slate-700 mb-1">
                {t('complianceTopology.complianceDrift', 'Compliance drift')}
              </h4>
              <ul className="text-xs text-slate-600 space-y-1">
                {data.drift.slice(0, 5).map((d, i) => (
                  <li key={d.id || i} title={d.description}>
                    {t('complianceTopology.driftDetected', 'Drift detected')}: {d.driftType} ({d.severity})
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {r?.explanations?.length ? (
            <div className="mt-3 border-t pt-2">
              <h4 className="text-xs font-semibold text-slate-700 mb-1">
                {t('complianceTopology.appliesBecause', 'Applies because')}
              </h4>
              <ul className="text-xs text-slate-500 space-y-0.5">
                {r.explanations.slice(0, 4).map((ex, i) => (
                  <li key={i}>{ex.message}</li>
                ))}
              </ul>
            </div>
          ) : null}
        </CardContent>
      </Card>
    </section>
  )
}
